import { useEffect, useState } from "react";
import { outreachFetch, useBusiness } from "../lib/business";
import { Link } from "react-router-dom";

interface Campaign {
  id: number; name: string; subject: string; status: string;
  from_email?: string | null; reply_to?: string | null;
  created_by_email?: string | null; created_at: string;
  scheduled_at?: string | null; sent_at?: string | null;
  sent_count: number; sent_ok: number; sent_failed: number;
  business_ids: number[];
}

export default function Campaigns() {
  const [list, setList] = useState<Campaign[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const { businesses } = useBusiness();

  function load() {
    outreachFetch("/api/outreach/campaigns")
      .then(r => r.json() as Promise<{ campaigns?: Campaign[] }>)
      .then(d => setList(d.campaigns || []))
      .catch(e => setErr(String(e)));
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Email templates</h1>
          <p className="mt-1 text-sm text-slate-500">Saved emails with placeholders. Each is available to the companies ticked on it; companies pick one when sending.</p>
        </div>
        <Link to="/outreach/campaigns/new" className="btn-primary">+ New template</Link>
      </div>

      {err && <div className="card p-4 text-rose-700">{err}</div>}

      <div className="card overflow-hidden">
        <table className="refined">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Subject</th>
              <th>Available to</th>
              <th className="w-24">Status</th>
              <th className="w-32 text-right">Sent / Failed</th>
              <th className="w-44">Created</th>
            </tr>
          </thead>
          <tbody>
            {list.map(c => (
              <tr key={c.id}>
                <td>{c.id}</td>
                <td className="font-medium text-slate-900">
                  <Link to={`/outreach/campaigns/${c.id}`} className="hover:underline">{c.name}</Link>
                </td>
                <td className="max-w-md truncate">{c.subject}</td>
                <td className="text-xs">
                  {c.business_ids.length
                    ? c.business_ids.map(id => <span key={id} className="pill pill-slate mr-1">{businesses.find(b => b.id === id)?.name || `#${id}`}</span>)
                    : <span className="text-amber-700">no company</span>}
                </td>
                <td><StatusPill v={c.status} scheduledAt={c.scheduled_at} /></td>
                <td className="text-right font-mono text-sm">
                  <span className="text-emerald-700">{c.sent_ok}</span> / <span className="text-rose-700">{c.sent_failed}</span>
                </td>
                <td className="text-xs text-slate-500">{new Date(c.created_at.replace(" ", "T") + "Z").toLocaleString()}</td>
              </tr>
            ))}
            {!list.length && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No templates yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ v, scheduledAt }: { v: string; scheduledAt?: string | null }) {
  // A campaign sits in status='sending' both while actively draining and while
  // waiting for a future scheduled_at. Distinguish the two for the user.
  if (v === "sending" && scheduledAt) {
    const when = new Date(scheduledAt.replace(" ", "T") + "Z");
    if (when.getTime() > Date.now()) {
      return (
        <span className="pill pill-blue" title={when.toLocaleString()}>
          scheduled · {when.toLocaleDateString(undefined, { day: "numeric", month: "short" })}
        </span>
      );
    }
  }
  const cls = {
    draft:   "pill pill-slate",
    sending: "pill pill-amber",
    sent:    "pill pill-green",
    paused:  "pill pill-rose",
  }[v] || "pill pill-slate";
  return <span className={cls}>{v}</span>;
}
